/* =========================
   PEGUU — STICKER PAYMENT
   BLOQUE DE PAGO DEL PASO 4
========================= */

(() => {
  "use strict";


  /* =========================
     CONFIGURACIÓN
  ========================= */

  const PURCHASE_CONFIRMATION_STORAGE_KEY =
    "peguu:purchase-confirmation";

  const PAYMENT_STORAGE_KEY =
    "peguu:sticker-payment";

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const ORDER_STORAGE_KEY =
    "peguu:sticker-order";

  const DESIGN_STORAGE_KEY =
    "peguu:sticker-design";

  const QR_STORAGE_KEY =
    "peguu:qr-config";

  const BILLING_STORAGE_KEY =
    "peguu:billing-data";

  const GUEST_CART_STORAGE_KEY =
    "peguu:guest-cart";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const CUSTOMERS_API_URL =
    window.PeguuApi.endpoint("customers");

  const PENDING_ORDER_STORAGE_KEY =
    "peguu:pending-order-id";

  const PURCHASE_CONFIRMATION_PATH =
    "/pages/resumen-compra";

  const SHOPIFY_CHECKOUT_API_URL =
    window.PeguuApi.endpoint("shopifyCheckout");

  const PAYMENT_STATUS_API_BASE_URL =
    window.PeguuApi.endpoint("customerOrders");

  const SAMPLE_PACK_API_URL =
    window.PeguuApi.endpoint("samplePack");



  /* =========================
     UTILIDADES DE STORAGE
  ========================= */

  function safeParse(value) {

    if (!value) {
      return null;
    }


    try {

      return JSON.parse(value);

    } catch (error) {

      return null;

    }

  }


  function readSessionObject(key) {

    return safeParse(
      sessionStorage.getItem(key)
    );

  }


  function writeSessionObject(
    key,
    value
  ) {

    try {

      sessionStorage.setItem(
        key,
        JSON.stringify(value)
      );


      return true;

    } catch (error) {

      console.warn(
        `PEGUU: no fue posible guardar ${key}.`,
        error
      );


      return false;

    }

  }


  function readGuestCartItems() {

    const stored =
      safeParse(
        window.localStorage.getItem(
          GUEST_CART_STORAGE_KEY
        )
      );

    return Array.isArray(stored)
      ? stored
      : [];

  }


  function writeGuestCartItems(items) {

    window.localStorage.setItem(
      GUEST_CART_STORAGE_KEY,
      JSON.stringify(items)
    );

  }


  function getLocalDesignFile(key) {

    const previewKey =
      String(key || "").trim();

    if (!previewKey || !window.indexedDB) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {

      const request =
        indexedDB.open(
          DESIGN_FILE_DB,
          1
        );

      request.onupgradeneeded = () => {

        if (
          !request.result.objectStoreNames
            .contains(DESIGN_FILE_STORE)
        ) {
          request.result.createObjectStore(
            DESIGN_FILE_STORE
          );
        }

      };

      request.onerror = () => resolve(null);

      request.onsuccess = () => {

        const database = request.result;
        const transaction =
          database.transaction(
            DESIGN_FILE_STORE,
            "readonly"
          );
        const getRequest = transaction
          .objectStore(DESIGN_FILE_STORE)
          .get(previewKey);

        getRequest.onsuccess = () => {
          resolve(getRequest.result || null);
          database.close();
        };

        getRequest.onerror = () => {
          resolve(null);
          database.close();
        };

      };

    });

  }


  async function uploadGuestDesignFile(file) {

    if (!file) {
      return null;
    }

    const token =
      String(
        window.PeguuAuth?.getSession?.()
          ?.token || ""
      ).trim();

    const headers = {
      Accept: "application/json"
    };

    if (token) {
      headers.Authorization =
        `Bearer ${token}`;
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await window.PeguuAuthClient.authFetch(
      `${CUSTOMERS_API_URL}/me/cart/design`,
      {
        method: "POST",
        headers,
        body: formData
      }
    );

    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (
      response.status !== 201 ||
      payload?.success === false
    ) {
      throw new Error(
        payload?.message ||
        "No pudimos cargar el archivo de diseño. Vuelve al paso de diseño e inténtalo de nuevo."
      );
    }

    const uploadedFile =
      payload?.data?.file ||
      payload?.data ||
      payload;

    const fileId =
      String(
        uploadedFile?.fileId ||
        uploadedFile?.id ||
        ""
      ).trim();

    if (!fileId) {
      throw new Error(
        "La respuesta de carga no incluyó el identificador del archivo."
      );
    }

    return {
      fileId,
      fileUrl:
        String(
          uploadedFile?.url ||
          uploadedFile?.fileUrl ||
          ""
        ).trim() || null
    };

  }


  async function ensureGuestItemDesignFile(item) {

    if (
      !item ||
      item.productCode === "sample_pack" ||
      item.design?.uploadLater === true
    ) {
      return item;
    }

    const currentFileId =
      String(
        item.fileId ||
        item.design?.fileId ||
        ""
      ).trim();

    if (currentFileId) {
      return {
        ...item,
        fileId: currentFileId
      };
    }

    const hasSelectedFile = Boolean(
      item.design?.fileName ||
      item.design?.localPreviewKey
    );

    if (!hasSelectedFile) {
      return item;
    }

    const file = await getLocalDesignFile(
      item.design?.localPreviewKey
    );

    if (!file) {
      throw new Error(
        "No encontramos el archivo de diseño. Vuelve al paso de diseño y súbelo de nuevo."
      );
    }

    const uploaded =
      await uploadGuestDesignFile(file);

    return {
      ...item,
      fileId: uploaded.fileId,
      design: {
        ...(item.design || {}),
        fileId: uploaded.fileId,
        fileUrl: uploaded.fileUrl,
        uploadStatus: "uploaded"
      }
    };

  }


  async function ensureGuestCartDesignFiles(items) {

    const nextItems = [];

    for (const item of items) {
      nextItems.push(
        await ensureGuestItemDesignFile(item)
      );
    }

    writeGuestCartItems(nextItems);

    return nextItems;

  }


  async function getPaymentStatus(
    peguuOrderId
  ) {

    const orderId =
      String(
        peguuOrderId ?? ""
      ).trim();


    if (!orderId) {
      throw new Error(
        "Falta el identificador del pedido."
      );
    }


    const session =
      window.PeguuAuth?.getSession?.() ||
      null;

    const headers = {
      Accept: "application/json"
    };


    if (session?.token) {
      headers.Authorization =
        `Bearer ${session.token}`;
    }


    const paymentStatusUrl =
      `${PAYMENT_STATUS_API_BASE_URL}/${encodeURIComponent(orderId)}/payment-status`;

    let response =
      await window.PeguuAuthClient.authFetch(
        paymentStatusUrl,
        { headers }
      );

    let responseData = null;


    try {
      responseData =
        await response.json();
    } catch (error) {
      responseData = null;
    }


    if (response.status === 403) {
      response =
        await window.fetch(
          paymentStatusUrl,
          {
            headers: {
              Accept:
                "application/json"
            }
          }
        );

      try {
        responseData =
          await response.json();
      } catch (error) {
        responseData = null;
      }
    }


    if (!response.ok) {
      const apiError =
        new Error(
          responseData?.message ||
          "No fue posible consultar el estado del pago."
        );

      apiError.status =
        response.status;

      throw apiError;
    }


    const data =
      responseData?.data ||
      responseData;

    const status =
      String(data?.status || "")
        .trim()
        .toLowerCase();

    const paymentStatus =
      String(
        data?.paymentStatus || ""
      )
        .trim()
        .toLowerCase();


    return {
      ...data,
      status,
      paymentStatus,
      isPaid:
        status === "paid" ||
        paymentStatus === "paid",
      isPending:
        status === "pending_payment" &&
        paymentStatus === "pending"
    };

  }


  window.PeguuCheckout =
    Object.freeze({
      getPaymentStatus
    });


  /* =========================
     INICIALIZACIÓN
  ========================= */

  function initPeguuStickerPayment(
    scope = document
  ) {

    const roots =
      scope.matches?.(
        "[data-peguu-sticker-payment]"
      )
        ? [scope]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-sticker-payment]"
            )
          );


    roots.forEach((root) => {

      const paymentMount =
        root.closest(
          "[data-peguu-payment-mount]"
        );


      /* =========================
         DIFERIR PAGO EMBEBIDO OCULTO
      ========================= */

      if (paymentMount?.hidden) {
        return;
      }


      /* =========================
         EVITAR DOBLE INIT
      ========================= */

      if (
        root.dataset
          .paymentInitialized ===
        "true"
      ) {
        return;
      }


      root.dataset
        .paymentInitialized =
        "true";

      const isSamplePackCheckout =
        root.dataset.paymentContext === "cart" &&
        new URLSearchParams(
          window.location.search
        ).get("mode") === "sample-pack";

      root.classList.toggle(
        "peguu-sticker-payment--sample-pack",
        isSamplePackCheckout
      );

      let samplePackMaterial = null;
      let samplePackError = null;


      /* =========================
         AUTENTICACIÓN
      ========================= */

      let isCustomerLoggedIn =
        false;


      let isLoginPending =
        false;

      let isGoogleLoginPending =
        false;

      let hasPositionedInitialState =
        false;

      const restoredPaymentState =
        readSessionObject(
          PAYMENT_STORAGE_KEY
        ) || {};

      function getRestoredCheckoutMode() {
        const paymentState =
          readSessionObject(
            PAYMENT_STORAGE_KEY
          ) || {};

        return (
          paymentState.step === "checkout" ||
          !paymentState.step
        ) &&
        paymentState.mode === "guest"
          ? "guest"
          : null;
      }

      function persistPaymentView(
        step,
        mode
      ) {
        if (
          !root.classList.contains(
            "peguu-sticker-payment--embedded"
          ) ||
          paymentMount?.hidden ||
          !paymentMount
            ?.closest(
              "[data-peguu-sticker-summary]"
            )
            ?.classList.contains(
              "is-payment-expanded"
            )
        ) {
          return;
        }

        writeSessionObject(
          PAYMENT_STORAGE_KEY,
          {
            ...(readSessionObject(
              PAYMENT_STORAGE_KEY
            ) || {}),
            isExpanded: true,
            step,
            mode
          }
        );
      }


      /* =========================
         ELEMENTOS PRINCIPALES
      ========================= */

      const accessSection =
        root.querySelector(
          "[data-payment-access]"
        );


      const checkout =
        root.querySelector(
          "[data-payment-checkout]"
        );

      function applyPaymentStep(
        step,
        mode = null
      ) {
        const isCheckout =
          step === "checkout";

        if (accessSection) {
          accessSection.hidden = isCheckout;
        }

        if (checkout) {
          checkout.hidden = !isCheckout;
        }

        root.dataset.paymentStep =
          isCheckout
            ? "checkout"
            : "access";

        root.dataset.checkoutMode =
          isCheckout
            ? mode === "guest"
              ? "guest"
              : "customer"
            : "access";

        persistPaymentView(
          root.dataset.paymentStep,
          root.dataset.checkoutMode
        );

        root.classList.remove(
          "peguu-sticker-payment--initializing"
        );
      }


      const authError =
        root.querySelector(
          "[data-payment-auth-error]"
        );


      /* =========================
         ACCESO
      ========================= */

      const loginButton =
        root.querySelector(
          "[data-payment-login]"
        );


      const loginForm =
        root.querySelector(
          "[data-payment-login-form]"
        );


      const googleLoginButton =
        root.querySelector(
          "[data-payment-google-login]"
        );


      const guestButton =
        root.querySelector(
          "[data-payment-guest]"
        );


      const returnToLoginButton =
        root.querySelector(
          "[data-return-to-login]"
        );


      const emailInput =
        root.querySelector(
          "[data-payment-email]"
        );


      if (
        emailInput &&
        !normalizeValue(emailInput.value) &&
        normalizeValue(
          restoredPaymentState.guestEmail
        )
      ) {
        emailInput.value =
          normalizeValue(
            restoredPaymentState.guestEmail
          );
      }


      const passwordInput =
        root.querySelector(
          "[data-payment-password]"
        );


      const loginButtonText =
        loginButton?.querySelector(
          ".peguu-button__text"
        );


      const defaultLoginButtonText =
        loginButtonText?.textContent
          ?.trim() ||
        "INICIAR SESIÓN";


      const googleLoginButtonText =
        googleLoginButton?.querySelector(
          "span"
        );


      const defaultGoogleLoginButtonText =
        googleLoginButtonText?.textContent
          ?.trim() ||
        "CONTINUAR CON GOOGLE";


      /* =========================
         CONTRASEÑA
      ========================= */

      const passwordToggle =
        root.querySelector(
          "[data-password-toggle]"
        );


      const eyeSlash =
        passwordToggle
          ?.querySelector(
            "[data-password-eye-slash]"
          );



      const contactPreferenceInputs =
        Array.from(
          root.querySelectorAll(
            "[data-contact-preference]"
          )
        );


      const contactPreferenceField =
        root.querySelector(
          "[data-contact-preference-field]"
        );


      const contactPreferenceError =
        root.querySelector(
          "[data-contact-preference-error]"
        );


      const contactEmailInput =
        root.querySelector(
          "[data-contact-email]"
        );


      const contactEmailError =
        root.querySelector(
          "[data-contact-email-error]"
        );


      const contactPhoneInput =
        root.querySelector(
          "[data-contact-phone]"
        );


      const contactPhoneError =
        root.querySelector(
          "[data-contact-phone-error]"
        );


      if (contactEmailInput) {
        contactEmailInput.value =
          normalizeValue(
            restoredPaymentState.contactEmail ||
            restoredPaymentState.guestEmail
          );
      }


      if (contactPhoneInput) {
        contactPhoneInput.value =
          normalizeValue(
            restoredPaymentState.contactPhone ||
            restoredPaymentState.guestPhone
          );
      }


      /* =========================
         FACTURACIÓN
      ========================= */

      const invoiceCheckbox =
        root.querySelector(
          "[data-needs-invoice]"
        );


      const paymentSubmit =
        root.querySelector(
          "[data-payment-submit]"
        );


      const paymentSubmitText =
        paymentSubmit?.querySelector(
          ".peguu-button__text"
        );

      const paymentProcessingOverlay =
        root.querySelector(
          "[data-payment-processing-overlay]"
        );


      if (
        paymentProcessingOverlay &&
        paymentProcessingOverlay.parentElement !== document.body
      ) {
        paymentProcessingOverlay.tabIndex = -1;
        document.body.appendChild(
          paymentProcessingOverlay
        );
      }


      let processingReturnFocus = null;


      const verifyEmailBanner =
        root.querySelector(
          "[data-payment-verify-email]"
        );


      const verifyEmailMessage =
        root.querySelector(
          "[data-payment-verify-email-message]"
        );


      const verifyEmailResend =
        root.querySelector(
          "[data-payment-verify-email-resend]"
        );


      let isCreatingOrder =
        false;

      let orderCreated =
        false;

      let paymentValidationShown =
        false;



      /* =========================================================
         HELPERS
      ========================================================= */

      function normalizeValue(value) {

        return String(
          value ?? ""
        ).trim();

      }


      function isEmailUnverified(
        session
      ) {

        return Boolean(
          session?.token &&
          session?.customer &&
          session.customer.emailVerified !==
            true
        );

      }


      function showVerifyEmailBanner(
        message
      ) {

        if (!verifyEmailBanner) {
          return;
        }


        if (
          verifyEmailMessage &&
          message
        ) {
          verifyEmailMessage.textContent =
            message;
        }


        verifyEmailBanner.hidden =
          false;

      }


      function hideVerifyEmailBanner() {

        if (!verifyEmailBanner) {
          return;
        }


        verifyEmailBanner.hidden =
          true;

      }


      function syncVerifyEmailBanner(
        session
      ) {

        if (
          isEmailUnverified(
            session
          )
        ) {
          showVerifyEmailBanner(
            "Tu 10% de descuento se activará cuando verifiques tu correo."
          );
          return;
        }


        hideVerifyEmailBanner();

      }




      function isGuestCheckout() {
        return root.dataset.checkoutMode === "guest";
      }


      function isValidContactEmail() {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          .test(
            normalizeValue(
              contactEmailInput?.value
            )
          );
      }


      function isValidContactPhone() {
        return /^[0-9]{10}$/
          .test(
            normalizeValue(
              contactPhoneInput?.value
            )
          );
      }


      function syncContactFieldValueState(input) {
        input
          ?.closest("[data-contact-field]")
          ?.classList.toggle(
            "has-value",
            normalizeValue(input?.value).length > 0
          );
      }


      function setContactFieldError(
        input,
        errorElement,
        isValid
      ) {
        input
          ?.closest("[data-contact-field]")
          ?.classList.toggle(
            "is-error",
            !isValid
          );

        if (errorElement) {
          errorElement.hidden = isValid;
        }
      }


      function validateContactDetails() {
        const isEmailValid =
          isValidContactEmail();
        const isPhoneValid =
          isValidContactPhone();

        setContactFieldError(
          contactEmailInput,
          contactEmailError,
          isEmailValid
        );
        setContactFieldError(
          contactPhoneInput,
          contactPhoneError,
          isPhoneValid
        );

        return isEmailValid && isPhoneValid;
      }


      function persistContactDetails() {
        const contactEmail =
          normalizeValue(
            contactEmailInput?.value
          ).toLowerCase();
        const contactPhone =
          normalizeValue(
            contactPhoneInput?.value
          );
        const storedPaymentState =
          readSessionObject(
            PAYMENT_STORAGE_KEY
          ) || {};

        writeSessionObject(
          PAYMENT_STORAGE_KEY,
          {
            ...storedPaymentState,
            contactEmail,
            contactPhone,
            ...(isGuestCheckout()
              ? {
                  guestEmail: contactEmail,
                  guestPhone: contactPhone
                }
              : {})
          }
        );
      }


      function getContactPreference() {

        return normalizeValue(
          contactPreferenceInputs.find(
            (input) => input.checked
          )?.value
        );

      }


      function isContactPreferenceComplete() {

        return [
          "whatsapp",
          "email",
          "both"
        ].includes(
          getContactPreference()
        );

      }


      function clearContactPreferenceError() {

        contactPreferenceField
          ?.classList
          .remove("is-error");


        if (contactPreferenceError) {
          contactPreferenceError.hidden = true;
        }

      }


      function validateContactPreference() {

        const isValid =
          isContactPreferenceComplete();


        contactPreferenceField
          ?.classList
          .toggle(
            "is-error",
            !isValid
          );


        if (contactPreferenceError) {
          contactPreferenceError.hidden =
            isValid;
        }


        return isValid;

      }


      /* =========================================================
         FACTURACIÓN
      ========================================================= */

      function getBillingData() {

        return readSessionObject(
          BILLING_STORAGE_KEY
        );

      }


      function hasValidBillingData() {

        const session =
          window.PeguuAuth
            ?.getSession?.() ||
          null;


        if (session?.token) {
          return Boolean(
            window.PeguuBilling
              ?.hasPersistedProfile?.()
          );
        }

        const billingData =
          getBillingData();


        return Boolean(
          billingData?.businessName &&
          billingData?.rfc &&
          billingData?.postalCode &&
          billingData?.taxRegime &&
          billingData?.cfdiUse
        );

      }


      function isBillingComplete() {

        if (
          !invoiceCheckbox?.checked
        ) {
          return true;
        }


        return hasValidBillingData();

      }


      function syncInvoiceCheckbox() {

        if (!invoiceCheckbox) {
          return;
        }


        invoiceCheckbox.checked = false;

      }


      /* =========================================================
         ESTADO DE AUTENTICACIÓN
      ========================================================= */

      function hideAuthError() {

        if (!authError) {
          return;
        }


        authError.textContent = "";
        authError.hidden = true;

      }


      function showAuthError(message) {

        if (!authError) {
          return;
        }


        authError.textContent =
          normalizeValue(message) ||
          "No fue posible iniciar sesión. Inténtalo de nuevo.";

        authError.hidden = false;

      }


      function setLoginLoading(isLoading) {

        isLoginPending =
          Boolean(isLoading);


        if (loginButton) {
          loginButton.disabled =
            isLoginPending;

          loginButton.setAttribute(
            "aria-busy",
            String(isLoginPending)
          );
        }


        if (emailInput) {
          emailInput.disabled =
            isLoginPending;
        }


        if (passwordInput) {
          passwordInput.disabled =
            isLoginPending;
        }


        if (loginButtonText) {
          loginButtonText.textContent =
            isLoginPending
              ? "INICIANDO…"
              : defaultLoginButtonText;
        }

      }


      function renderAuthenticationState({
        isAuthenticated,
        customer = null,
        mode = null
      }) {

        isCustomerLoggedIn =
          Boolean(
            isAuthenticated &&
            customer
          );


        root.dataset
          .paymentAuthPending =
          "false";


        applyPaymentStep(
          isCustomerLoggedIn || mode === "guest"
            ? "checkout"
            : "access",
          isCustomerLoggedIn
            ? "customer"
            : mode
        );


        if (isCustomerLoggedIn) {
          hideAuthError();

          if (
            contactEmailInput &&
            !normalizeValue(
              contactEmailInput.value
            )
          ) {
            contactEmailInput.value =
              normalizeValue(
                customer?.email
              );
          }

          if (
            contactPhoneInput &&
            !normalizeValue(
              contactPhoneInput.value
            )
          ) {
            contactPhoneInput.value =
              normalizeValue(
                customer?.phone ||
                customer?.phoneNumber
              ).replace(/\D/g, "").slice(-10);
          }

          syncContactFieldValueState(
            contactEmailInput
          );
          syncContactFieldValueState(
            contactPhoneInput
          );
          persistContactDetails();

          syncVerifyEmailBanner(
            window.PeguuAuth
              ?.getSession?.() ||
            {
              token: "1",
              customer
            }
          );

        }


        if (!isCustomerLoggedIn) {
          hideVerifyEmailBanner();
        }


        updatePaymentSubmitState();

        if (
          !hasPositionedInitialState &&
          root.classList.contains(
            "peguu-sticker-payment--embedded"
          ) &&
          root.dataset
            .deferInitialPosition !==
            "true"
        ) {
          hasPositionedInitialState = true;

          scrollToPaymentState(
            isCustomerLoggedIn || mode === "guest"
              ? getCheckoutScrollTarget()
              : accessSection
          );
        }

      }


      async function restoreAuthenticationState() {

        const restoredCheckoutMode =
          getRestoredCheckoutMode();

        let auth =
          window.PeguuAuth;


        if (
          !auth ||
          typeof auth.validateSession !==
            "function"
        ) {

          await new Promise((resolve) => {
            window.setTimeout(resolve, 0);
          });


          auth =
            window.PeguuAuth;

        }


        if (
          !auth ||
          typeof auth.validateSession !==
            "function"
        ) {

          renderAuthenticationState({
            isAuthenticated:
              false,
            mode: restoredCheckoutMode
          });

          return;
        }


        try {

          let session =
            await auth.validateSession();


          if (
            session?.token &&
            typeof auth.refreshCustomer ===
              "function"
          ) {
            try {
              session =
                await auth.refreshCustomer() ||
                session;
            } catch (error) {
              console.warn(
                "PEGUU: no fue posible actualizar el perfil del cliente.",
                error
              );
            }
          }


          if (session?.token) {
            try {
              await window.PeguuBilling
                ?.loadProfile?.(
                  session
                );
            } catch (error) {
              console.warn(
                "PEGUU: no fue posible consultar el perfil fiscal.",
                {
                  status:
                    error?.status ||
                    null
                }
              );
            }
          }


          renderAuthenticationState({
            isAuthenticated:
              Boolean(
                session?.token &&
                session?.customer
              ),

            customer:
              session?.customer ||
              null,
            mode: restoredCheckoutMode
          });

        } catch (error) {

          renderAuthenticationState({
            isAuthenticated: false,
            mode: restoredCheckoutMode
          });


          showAuthError(
            error?.status === 401
              ? "Tu sesión expiró. Inicia sesión nuevamente."
              : "No fue posible verificar tu sesión. Inicia sesión para continuar."
          );

        }

      }


      /* =========================================================
         MOSTRAR CHECKOUT
      ========================================================= */

      function scrollToPaymentState(element) {
        if (!element) {
          return;
        }

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (
              element === checkout &&
              root.dataset.checkoutMode ===
                "guest"
            ) {
              const scrollMarginTop =
                Number.parseFloat(
                  window.getComputedStyle(element)
                    .scrollMarginTop
                ) || 0;
              const guestContextOffset =
                window.matchMedia(
                  "(max-width: 749px)"
                ).matches
                  ? 20
                  : 40;

              window.scrollTo({
                top: Math.max(
                  0,
                  window.scrollY +
                    element.getBoundingClientRect().top -
                    scrollMarginTop -
                    guestContextOffset
                ),
                behavior: "smooth"
              });

              return;
            }

            if (
              element.matches(
                ".peguu-cart-order-summary__grand-total"
              )
            ) {
              const scrollMarginTop =
                Number.parseFloat(
                  window.getComputedStyle(element)
                    .scrollMarginTop
                ) || 0;
              const checkoutOffset =
                window.matchMedia(
                  "(max-width: 749px)"
                ).matches
                  ? 32
                  : 48;

              window.scrollTo({
                top: Math.max(
                  0,
                  window.scrollY +
                    element.getBoundingClientRect().top -
                    scrollMarginTop +
                    checkoutOffset
                ),
                behavior: "smooth"
              });

              return;
            }

            if (
              element.matches(
                ".peguu-sticker-payment__access"
              )
            ) {
              const accessViewportTop =
                window.matchMedia(
                  "(max-width: 749px)"
                ).matches
                  ? 126
                  : 188;

              const positionAccess = () => {
                if (accessSection?.hidden) {
                  return;
                }

                window.scrollTo({
                  top: Math.max(
                    0,
                    window.scrollY +
                      element.getBoundingClientRect().top -
                      accessViewportTop
                  ),
                  behavior: "smooth"
                });
              };

              const summary =
                root.closest(
                  "[data-peguu-sticker-summary]"
                );

              if (
                summary?.classList.contains(
                  "is-payment-expanded"
                ) &&
                !summary.classList.contains(
                  "is-payment-settled"
                )
              ) {
                window.setTimeout(
                  positionAccess,
                  460
                );
              } else {
                positionAccess();
              }

              return;
            }

            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          });
        });
      }

      function getCheckoutScrollTarget() {
        const paymentMount =
          root.closest(
            "[data-peguu-payment-mount]"
          );

        const summary =
          paymentMount?.closest(
            "[data-peguu-sticker-summary]"
          );

        const welcome = root.querySelector(
          "[data-peguu-summary-welcome]"
        );

        if (welcome && !welcome.hidden) {
          return welcome;
        }

        if (
          root.dataset.checkoutMode ===
          "guest"
        ) {
          return checkout;
        }

        return root.querySelector(
          "[data-contact-section]"
        ) || summary?.querySelector(
          ".peguu-cart-order-summary__grand-total"
        ) || paymentMount || checkout;
      }

      function showCheckout(mode) {
        applyPaymentStep(
          "checkout",
          mode
        );

        updatePaymentSubmitState();


        scrollToPaymentState(
          getCheckoutScrollTarget()
        );

      }


      function returnToAccess() {

        if (
          isCustomerLoggedIn ||
          root.dataset.checkoutMode !==
            "guest"
        ) {
          return;
        }


        applyPaymentStep("access");


        scrollToPaymentState(accessSection);

        window.setTimeout(() => {
          emailInput?.focus({
            preventScroll: true
          });
        }, 350);

      }


      /* =========================================================
         LOGIN REAL
      ========================================================= */

      async function handleLogin() {

        if (isLoginPending) {
          return;
        }


        hideAuthError();


        const email =
          normalizeValue(
            emailInput?.value
          ).toLowerCase();


        const password =
          String(
            passwordInput?.value ||
            ""
          );


        if (!email || !password) {
          showAuthError(
            "Completa tu email y contraseña."
          );

          (!email
            ? emailInput
            : passwordInput
          )?.focus();

          return;
        }


        if (
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/
            .test(email)
        ) {
          showAuthError(
            "Ingresa un correo electrónico válido."
          );

          emailInput?.focus();

          return;
        }


        if (
          !window.PeguuAuth ||
          typeof window.PeguuAuth.login !==
            "function"
        ) {
          showAuthError(
            "No fue posible iniciar sesión. Inténtalo de nuevo."
          );

          return;
        }


        setLoginLoading(true);


        try {

          const session =
            await window.PeguuAuth.login({
              email,
              password
            });


          renderAuthenticationState({
            isAuthenticated: true,
            customer:
              session?.customer ||
              null
          });

          scrollToPaymentState(
            getCheckoutScrollTarget()
          );


          if (passwordInput) {
            passwordInput.value = "";
          }
          syncPasswordToggle();

        } catch (error) {

          const apiMessage =
            normalizeValue(
              error?.message
            );


          showAuthError(
            apiMessage &&
            ![
              "Failed to fetch",
              "Load failed",
              "NetworkError when attempting to fetch resource."
            ].includes(apiMessage)
              ? apiMessage
              : "No fue posible iniciar sesión. Inténtalo de nuevo."
          );

        } finally {

          setLoginLoading(false);

        }

      }


      function handleGoogleLogin() {

        hideAuthError();


        if (
          isGoogleLoginPending
        ) {
          return;
        }


        if (
          !window.PeguuAuth ||
          typeof window.PeguuAuth
            .startShopifyLogin !==
            "function"
        ) {
          showAuthError(
            "No fue posible iniciar el acceso con Google. Inténtalo de nuevo."
          );

          return;
        }


        isGoogleLoginPending = true;
        googleLoginButton.disabled = true;
        googleLoginButton.setAttribute(
          "aria-busy",
          "true"
        );


        if (googleLoginButtonText) {
          googleLoginButtonText.textContent =
            "REDIRIGIENDO…";
        }


        try {
          window.PeguuAuth
            .startShopifyLogin({
              openModalAfterLogin: false
            });
        } catch (error) {
          isGoogleLoginPending = false;
          googleLoginButton.disabled = false;
          googleLoginButton.setAttribute(
            "aria-busy",
            "false"
          );


          if (googleLoginButtonText) {
            googleLoginButtonText.textContent =
              defaultGoogleLoginButtonText;
          }


          showAuthError(
            normalizeValue(
              error?.message
            ) ||
            "No fue posible iniciar el acceso con Google. Inténtalo de nuevo."
          );
        }

      }


      /* =========================================================
         INVITADO
      ========================================================= */

      function handleGuest() {

        hideAuthError();

        const accessEmail =
          normalizeValue(
            emailInput?.value
          ).toLowerCase();

        if (
          accessEmail &&
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            .test(accessEmail)
        ) {
          if (contactEmailInput) {
            contactEmailInput.value =
              accessEmail;
            syncContactFieldValueState(
              contactEmailInput
            );
          }

          writeSessionObject(
            PAYMENT_STORAGE_KEY,
            {
              ...(readSessionObject(
                PAYMENT_STORAGE_KEY
              ) || {}),
              guestEmail: accessEmail,
              contactEmail: accessEmail
            }
          );
        }

        showCheckout(
          "guest"
        );

      }


      /* =========================================================
         CONTRASEÑA
      ========================================================= */

      function syncPasswordToggle() {

        if (
          !passwordInput ||
          !passwordToggle
        ) {
          return;
        }


        const isVisible =
          passwordInput.type ===
          "text";


        passwordToggle.setAttribute(
          "aria-pressed",
          String(isVisible)
        );


        passwordToggle.setAttribute(
          "aria-label",
          isVisible
            ? "Ocultar contraseña"
            : "Mostrar contraseña"
        );


        if (eyeSlash) {

          eyeSlash.style.display =
            isVisible
              ? "none"
              : "";

        }

      }


      function togglePassword() {

        if (!passwordInput) {
          return;
        }


        const isHidden =
          passwordInput.type ===
          "password";


        passwordInput.type =
          isHidden
            ? "text"
            : "password";


        syncPasswordToggle();


        passwordInput.focus();


        try {

          const length =
            passwordInput
              .value
              .length;


          passwordInput
            .setSelectionRange(
              length,
              length
            );

        } catch (error) {

          /* Sin acción */

        }

      }

      /* =========================================================
         VALIDAR FORMULARIO
      ========================================================= */

      function validateCheckoutDetails() {

        const areContactDetailsValid =
          validateContactDetails();
        const isContactValid =
          validateContactPreference();


        if (!areContactDetailsValid) {

          const invalidContactInput =
            !isValidContactEmail()
              ? contactEmailInput
              : contactPhoneInput;

          invalidContactInput
            ?.closest("[data-contact-field]")
            ?.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });

          window.setTimeout(() => {
            invalidContactInput?.focus({
              preventScroll: true
            });
          }, 350);

        } else if (!isContactValid) {

          contactPreferenceField
            ?.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });


          window.setTimeout(() => {
            contactPreferenceInputs[0]
              ?.focus({
                preventScroll: true
              });
          }, 350);

        }


        return areContactDetailsValid &&
          isContactValid;

      }

      function showPaymentValidationAlert(
        issues,
        { serverError = false } = {}
      ) {
        const isServerError =
          serverError ||
          (
            issues.length === 1 &&
            /checkout|pedido|carrito|precio|servidor|crear/i.test(issues[0])
          );

        if (!issues.length) {
          window.PeguuValidationAlert?.close();
          return;
        }

        window.PeguuValidationAlert?.open({
          title: isServerError
            ? "No pudimos iniciar el checkout"
            : "Faltan datos para continuar",
          message: isServerError
            ? issues[0]
            : "Para continuar, complete los campos de:",
          fields: isServerError ? [issues[0]] : issues,
          showFields: !isServerError,
        });
      }

      function closePaymentValidationAlert() {
        window.PeguuValidationAlert?.close();
        paymentValidationShown = false;
      }

      function getPaymentValidationIssues() {
        const issues = [];

        if (!isValidContactEmail()) {
          issues.push("Correo electrónico");
        }

        if (!isValidContactPhone()) {
          issues.push("Número de teléfono");
        }

        if (!isContactPreferenceComplete()) {
          issues.push("Elige una preferencia de contacto");
        }

        return [...new Set(issues)];
      }

      function refreshPaymentValidationAlert() {
        if (!paymentValidationShown) return;
        showPaymentValidationAlert(getPaymentValidationIssues());
      }


      /* =========================================================
         ESTADO DEL BOTÓN PAGAR
      ========================================================= */

      function updatePaymentSubmitState() {

        if (!paymentSubmit) {
          return;
        }


        const canSubmit =
          !isCreatingOrder &&
          !orderCreated &&
          isValidContactEmail() &&
          isValidContactPhone() &&
          isContactPreferenceComplete() &&
          isBillingComplete() &&
          (
            !isSamplePackCheckout ||
            Boolean(samplePackMaterial)
          );


        paymentSubmit.disabled =
          isCreatingOrder || orderCreated;


        paymentSubmit.setAttribute(
          "aria-disabled",
          String(isCreatingOrder || orderCreated)
        );


        paymentSubmit.classList.toggle(
          "is-disabled",
          isCreatingOrder || orderCreated
        );


        const isProcessing =
          isCreatingOrder ||
          orderCreated;


        paymentSubmit.classList.toggle(
          "is-processing",
          isProcessing
        );


        paymentSubmit.setAttribute(
          "aria-busy",
          String(isProcessing)
        );


        if (paymentProcessingOverlay) {
          paymentProcessingOverlay.hidden =
            !isProcessing;

          if (isProcessing) {
            if (
              !processingReturnFocus &&
              document.activeElement instanceof HTMLElement
            ) {
              processingReturnFocus =
                document.activeElement;
            }

            document.documentElement.classList.add(
              "peguu-payment-processing"
            );
            document.body.classList.add(
              "peguu-payment-processing"
            );

            paymentProcessingOverlay.focus({
              preventScroll: true
            });
          } else {
            const hasActiveProcessingOverlay =
              Array.from(
                document.querySelectorAll(
                  "[data-payment-processing-overlay]"
                )
              ).some((overlay) => !overlay.hidden);

            if (!hasActiveProcessingOverlay) {
              document.documentElement.classList.remove(
                "peguu-payment-processing"
              );
              document.body.classList.remove(
                "peguu-payment-processing"
              );

              if (
                processingReturnFocus?.isConnected
              ) {
                processingReturnFocus.focus({
                  preventScroll: true
                });
              }
            }

            processingReturnFocus = null;
          }
        }


        if (paymentSubmitText) {
          paymentSubmitText.textContent =
            isProcessing
              ? "PROCESANDO…"
              : "PAGAR TU PEDIDO";
        }

      }



      /* =========================================================
         DATOS PARA CONFIRMACIÓN
      ========================================================= */



      function getDesignName(
        order,
        designState
      ) {

        const uploadLater =
          order?.design
            ?.uploadLater ===
            true ||

          designState
            ?.uploadLater ===
            true;


        if (uploadLater) {
          return "Lo subiré más tarde";
        }


        return (
          order?.design
            ?.fileName ||

          designState
            ?.primaryFile
            ?.fileName ||

          "Sin diseño"
        );

      }


      function getPreviewImage(
        order,
        material
      ) {

        const designPreview =
          normalizeValue(
            order?.design
              ?.previewUrl
          );


        if (
          designPreview &&
          !designPreview.startsWith(
            "blob:"
          )
        ) {

          return designPreview;

        }


        return (
          material.imageUrl ||

          order?.material
            ?.imageUrl ||

          ""
        );

      }




      function buildGuestBillingPayload() {

        const requiresInvoice =
          Boolean(
            invoiceCheckbox?.checked
          );


        if (!requiresInvoice) {
          return {
            requiresInvoice: false
          };
        }


        const billingData =
          getBillingData();


        return {
          requiresInvoice: true,
          billing: {
            legalName:
              normalizeValue(
                billingData?.businessName
              ),
            rfc:
              normalizeValue(
                billingData?.rfc
              ).toUpperCase(),
            postalCode:
              normalizeValue(
                billingData?.postalCode
              ),
            address: null,
            cfdiUse:
              normalizeValue(
                billingData?.cfdiUse
              ),
            taxRegime:
              normalizeValue(
                billingData?.taxRegime
              )
          }
        };

      }


      function getCheckoutContactData() {
        const storedPaymentState =
          readSessionObject(
            PAYMENT_STORAGE_KEY
          ) || {};

        return {
          email:
            normalizeValue(
              contactEmailInput?.value ||
              storedPaymentState.contactEmail ||
              storedPaymentState.guestEmail
            ).toLowerCase(),
          phone:
            normalizeValue(
              contactPhoneInput?.value ||
              storedPaymentState.contactPhone ||
              storedPaymentState.guestPhone
            )
        };
      }


      function buildGuestPayload() {
        const {
          email,
          phone
        } = getCheckoutContactData();


        if (
          !email ||
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/
            .test(email)
        ) {
          const error = new Error(
            "Ingresa un correo electrónico válido."
          );

          error.code =
            "INVALID_GUEST_EMAIL";

          throw error;
        }


        if (
          !/^[0-9]{10}$/.test(phone)
        ) {
          const error = new Error(
            "Ingresa un número de teléfono de 10 dígitos."
          );

          error.code =
            "INVALID_GUEST_PHONE";

          throw error;
        }


        return {
          guest: {
            email,
            phone
          },
          shippingAddress: {
            email,
            phone
          }
        };

      }


      function normalizeOrderQrConfig(
        item
      ) {

        const qrConfig =
          item?.qrConfig ||
          null;


        const isQrProduct =
          normalizeValue(
            item?.productCode
          ).toLowerCase() ===
            "qr_sticker" ||
          Boolean(qrConfig);


        if (!isQrProduct) {
          return null;
        }


        const qrUrl =
          normalizeValue(
            qrConfig?.link ||
            qrConfig?.destinationUrl ||
            qrConfig?.url ||
            ""
          );


        const acabado =
          normalizeValue(
            qrConfig?.acabado
          );


        const posicion =
          normalizeValue(
            qrConfig?.posicion
          );

        const allowedFinishes =
          ["mate", "brillante"];

        const allowedPositions =
          ["arriba", "abajo", "izquierda", "derecha"];


        if (
          !qrUrl ||
          !allowedFinishes.includes(acabado) ||
          !allowedPositions.includes(posicion)
        ) {
          const error =
            new Error(
              "Revisa la configuración QR antes de continuar."
            );

          error.code =
            "INVALID_QR_CONFIG";

          throw error;
        }


        return {
          link:
            qrUrl,

          acabado,
          posicion
        };

      }


      function normalizeGuestOrderItem(
        item
      ) {

        const materialId =
          normalizeValue(
            item?.materialId
          );


        if (
          normalizeValue(
            item?.productCode
          ) === "sample_pack"
        ) {
          if (!materialId) {
            const error =
              new Error(
                "El paquete de muestras no tiene un material válido."
              );

            error.code =
              "INVALID_CART_ITEM";

            throw error;
          }

          return {
            materialId
          };
        }


        const widthCm =
          Number(item?.widthCm);


        const heightCm =
          Number(item?.heightCm);


        const quantity =
          Number(item?.quantity);


        if (
          !materialId ||
          !Number.isFinite(widthCm) ||
          widthCm <= 0 ||
          !Number.isFinite(heightCm) ||
          heightCm <= 0 ||
          !Number.isInteger(quantity) ||
          quantity <= 0
        ) {
          const error =
            new Error(
              "Revisa los productos de tu carrito antes de continuar."
            );

          error.code =
            "INVALID_CART_ITEM";

          throw error;
        }


        return {
          materialId,
          widthCm,
          heightCm,
          quantity,

          fileId:
            normalizeValue(
              item?.fileId ||
              item?.design?.fileId
            ) || null,

          fileId2:
            normalizeValue(
              item?.fileId2 ||
              item?.design2?.fileId ||
              item?.secondaryDesign?.fileId
            ) || null,

          qrConfig:
            normalizeOrderQrConfig(
              item
            )
        };

      }


      function getCheckoutCouponCode(
        session,
        source,
        cart
      ) {

        const appliedCode =
          (Array.isArray(cart?.coupons)
            ? cart.coupons
            : cart?.coupon
              ? [cart.coupon]
              : [])
            .map((coupon) =>
              normalizeValue(coupon?.code).toUpperCase()
            )
            .find((code) => code && code !== "CLIENTEPEGUU") || "";


        if (appliedCode) {
          return appliedCode;
        }


        if (cart) {
          return "";
        }


        const appliedCodes =
          window.PeguuCart
            ?.getAppliedCouponCodes?.();

        if (Array.isArray(appliedCodes)) {
          return normalizeValue(appliedCodes[0]).toUpperCase();
        }

        return normalizeValue(
          window.PeguuCart?.getAppliedCouponCode?.()
        ).toUpperCase();

      }


      function withCheckoutCoupon(
        payload,
        session,
        source,
        cart
      ) {

        const couponCode =
          getCheckoutCouponCode(
            session,
            source,
            cart
          );


        if (couponCode) {
          payload.couponCodes =
            [couponCode];
        }


        return payload;

      }


      function validateCartForOrder(
        cart
      ) {

        const items =
          Array.isArray(cart?.items)
            ? cart.items
            : [];


        if (!items.length) {
          const error =
            new Error(
              "Tu carrito está vacío."
            );

          error.code =
            "EMPTY_CART";

          throw error;
        }


        if (
          items.some(
            (item) =>
              item?.priceValid === false
          )
        ) {
          const error =
            new Error(
              "El precio de uno de los productos necesita actualizarse antes de continuar."
            );

          error.code =
            "INVALID_PRICE";

          throw error;
        }


        items.forEach(
          normalizeOrderQrConfig
        );


        return items;

      }


      async function getSamplePackMaterial() {

        if (samplePackMaterial) {
          return samplePackMaterial;
        }


        if (samplePackError) {
          throw samplePackError;
        }


        try {
          const sharedMaterial =
            await window.PeguuSamplePackCheckout
              ?.getMaterial?.();


          if (sharedMaterial) {
            samplePackMaterial =
              sharedMaterial;

            return samplePackMaterial;
          }


          const response =
            await fetch(
              SAMPLE_PACK_API_URL,
              {
                headers: {
                  Accept:
                    "application/json"
                }
              }
            );


          let payload = null;


          try {
            payload =
              await response.json();
          } catch (error) {
            payload = null;
          }


          if (
            !response.ok ||
            payload?.success !== true
          ) {
            throw new Error(
              payload?.message ||
              "No fue posible consultar el paquete de muestras."
            );
          }


          samplePackMaterial =
            payload.data;


          if (
            !samplePackMaterial?.id ||
            samplePackMaterial?.active === false ||
            samplePackMaterial?.productCode !==
              "sample_pack"
          ) {
            throw new Error(
              "El paquete de muestras no está disponible."
            );
          }


          return samplePackMaterial;
        } catch (error) {
          samplePackError =
            error instanceof Error
              ? error
              : new Error(
                  "No fue posible consultar el paquete de muestras."
                );

          throw samplePackError;
        }

      }


      async function buildSamplePackCheckoutPayload(
        session
      ) {

        const material =
          await getSamplePackMaterial();

        const items = [
          {
            materialId:
              material.id
          }
        ];


        if (session?.token) {
          const payload = {
            source: "buy_now",
            preferredChannel:
              getContactPreference() ||
              "email",
            items,
            requiresInvoice:
              Boolean(
                invoiceCheckbox
                  ?.checked
              ),
            shippingAddress:
              getCheckoutContactData()
          };


          if (payload.requiresInvoice) {
            payload.billingProfileId =
              window.PeguuBilling
                ?.getProfileId?.();
          }


          return withCheckoutCoupon(
            payload,
            session,
            "buy_now"
          );
        }


        return withCheckoutCoupon(
          {
            source: "buy_now",

            preferredChannel:
              getContactPreference() ||
              "email",

            ...buildGuestPayload(),
            items,
            ...buildGuestBillingPayload()
          },
          session,
          "buy_now"
        );

      }


      async function buildStoreOrderPayload(
        session
      ) {

        if (session?.token) {
          const cart =
            typeof window.PeguuCart
              ?.mergeGuestCart ===
              "function"
              ? await window.PeguuCart
                  .mergeGuestCart()
              : await window.PeguuCart
                  ?.getCurrentCart?.();


          validateCartForOrder(
            cart
          );


          const payload = {
            source: "cart",
            preferredChannel:
              getContactPreference() ||
              "email",
            requiresInvoice:
              Boolean(
                invoiceCheckbox
                  ?.checked
              ),
            shippingAddress:
              getCheckoutContactData()
          };


          if (payload.requiresInvoice) {
            payload.billingProfileId =
              window.PeguuBilling
                ?.getProfileId?.();
          }


          return withCheckoutCoupon(
            payload,
            session,
            "cart"
          );
        }


        const currentCart =
          await window.PeguuCart
            ?.getCurrentCart?.();


        const storedGuestItems =
          window.PeguuCart
            ?.getGuestCart?.();

        const guestItems =
          await ensureGuestCartDesignFiles(
            Array.isArray(storedGuestItems)
              ? storedGuestItems
              : readGuestCartItems()
          );


        validateCartForOrder({
          items: guestItems
        });


        return withCheckoutCoupon(
          {
            source: "buy_now",

            preferredChannel:
              getContactPreference() ||
              "email",

            ...buildGuestPayload(),
            items:
              guestItems.map(
                normalizeGuestOrderItem
              ),

            ...buildGuestBillingPayload()
          },
          session,
          "buy_now",
          currentCart
        );

      }


      async function buildUnverifiedCustomerCheckoutPayload() {

        const currentCart =
          await window.PeguuCart
            ?.getCurrentCart?.();

        const items =
          validateCartForOrder(
            currentCart
          );


        return {
          source: "buy_now",

          preferredChannel:
            getContactPreference() ||
            "email",

          ...buildGuestPayload(),

          items:
            items.map(
              normalizeGuestOrderItem
            ),

          ...buildGuestBillingPayload()
        };

      }


      async function createStoreOrder(
        payload,
        session
      ) {

        const headers = {
          "Content-Type":
            "application/json",

          Accept:
            "application/json"
        };


        if (session?.token) {
          headers.Authorization =
            `Bearer ${session.token}`;
        }


        const checkoutFetch =
          session?.token
            ? window.PeguuAuthClient.authFetch
            : window.fetch.bind(window);


        const response =
          await checkoutFetch(
            SHOPIFY_CHECKOUT_API_URL,
            {
              method: "POST",
              headers,
              body:
                JSON.stringify(payload)
            }
          );


        let responseData = null;


        try {
          responseData =
            await response.json();

        } catch (error) {
          responseData = null;
        }


        if (
          !response.ok ||
          responseData?.success !== true ||
          !responseData?.data?.peguuOrderId ||
          !responseData?.data?.checkoutUrl
        ) {
          const responseError =
            [
              responseData?.message,
              responseData?.error?.message,
              typeof responseData?.error === "string"
                ? responseData.error
                : "",
              responseData?.detail,
              responseData?.data?.message,
              Array.isArray(responseData?.errors)
                ? responseData.errors.find(
                    (item) =>
                      typeof item === "string"
                  )
                : "",
              Array.isArray(responseData?.errors)
                ? responseData.errors.find(
                    (item) =>
                      typeof item?.message === "string"
                  )?.message
                : ""
            ].find(
              (value) =>
                typeof value === "string" &&
                value.trim()
            ) ||
            "No pudimos iniciar el checkout. Inténtalo nuevamente.";

          const apiError =
            new Error(
              responseError
            );

          apiError.status =
            response.status;

          apiError.code =
            responseData?.code ||
            null;

          throw apiError;
        }


        return responseData.data;

      }


      async function createSamplePackCheckout(
        payload,
        session
      ) {
        return createStoreOrder(
          payload,
          session
        );

      }


      function savePurchaseConfirmation(
        order
      ) {

        return writeSessionObject(
          PURCHASE_CONFIRMATION_STORAGE_KEY,
          {
            id:
              order.id,

            orderNumber:
              order.orderNumber,

            status:
              order.status,

            subtotal:
              order.subtotal,

            discountAmount:
              order.discountAmount,

            couponCode:
              order.couponCode,

            shippingCost:
              order.shippingCost,

            total:
              order.total,

            currency:
              order.currency,

            createdAt:
              order.createdAt ||
              new Date().toISOString()
          }
        );

      }


      function clearGuestPurchaseState() {

        [
          MATERIAL_STORAGE_KEY,
          ORDER_STORAGE_KEY,
          DESIGN_STORAGE_KEY,
          QR_STORAGE_KEY,
          PAYMENT_STORAGE_KEY,
          BILLING_STORAGE_KEY
        ].forEach((key) => {
          sessionStorage.removeItem(key);
        });

      }


      /* =========================================================
         PAGAR
      ========================================================= */

      async function handlePaymentSubmit(event) {
        event?.preventDefault();

        if (isCreatingOrder || orderCreated) {
          return;
        }

        const session =
          window.PeguuAuth?.getSession?.() ||
          null;

        paymentValidationShown = true;
        const checkoutDetailsValid = validateCheckoutDetails();
        const validationIssues = getPaymentValidationIssues();

        if (!checkoutDetailsValid || validationIssues.length) {
          if (invoiceCheckbox?.checked && !hasValidBillingData()) {
            if (session?.token) {
              window.PeguuBilling?.showProfileError?.(invoiceCheckbox);
            } else {
              window.PeguuBilling?.open?.(invoiceCheckbox);
            }
          }
          showPaymentValidationAlert(validationIssues);
          updatePaymentSubmitState();
          return;
        }

        if (
          invoiceCheckbox?.checked &&
          !hasValidBillingData()
        ) {
          closePaymentValidationAlert();
          if (session?.token) {
            window.PeguuBilling
              ?.showProfileError?.(
                invoiceCheckbox
              );
          } else {
            showPaymentValidationAlert(["completa y acepta los datos de facturación"]);
            window.PeguuBilling?.open(
              invoiceCheckbox
            );
          }
          refreshPaymentValidationAlert();
          updatePaymentSubmitState();
          return;
        }

        if (isEmailUnverified(session)) {
          showVerifyEmailBanner(
            "Tu 10% de descuento se activará cuando verifiques tu correo."
          );
        }

        isCreatingOrder = true;
        updatePaymentSubmitState();

        try {
          const payload =
            isSamplePackCheckout
              ? await buildSamplePackCheckoutPayload(
                  session
                )
              : await buildStoreOrderPayload(
                  session
                );

          const checkout =
            isSamplePackCheckout
              ? await createSamplePackCheckout(
                  payload,
                  session
                )
              : await createStoreOrder(
                  payload,
                  session
                );

          try {
            sessionStorage.setItem(
              PENDING_ORDER_STORAGE_KEY,
              checkout.peguuOrderId
            );

            localStorage.setItem(
              PENDING_ORDER_STORAGE_KEY,
              checkout.peguuOrderId
            );
          } catch (storageError) {
            throw new Error(
              "No fue posible guardar el pedido pendiente antes de redirigir."
            );
          }

          orderCreated = true;
          root.dataset.paymentReady =
            "true";
          window.location.assign(
            checkout.checkoutUrl
          );
        } catch (error) {
          const status =
            Number(error?.status) ||
            null;
          let message =
            error?.message ||
            "No pudimos crear tu pedido. Inténtalo nuevamente.";

          if (!isSamplePackCheckout) {
            if (
              error?.code === "EMPTY_CART"
            ) {
              message =
                "Tu carrito está vacío.";
            } else if (
              error?.code === "INVALID_PRICE"
            ) {
              message =
                "El precio de uno de los productos necesita actualizarse antes de continuar.";
            } else if (
              status === 400 ||
              error?.code === "INVALID_CART_ITEM" ||
              error?.code === "INVALID_QR_CONFIG"
            ) {
              message = error?.message ||
                "Revisa los datos del pedido y vuelve a intentarlo.";
            }
          }

          console.error(
            "PEGUU: Falló /customers/me/shopify-checkout.",
            { status }
          );
          showPaymentValidationAlert(
            [message],
            { serverError: true }
          );
        } finally {
          isCreatingOrder = false;
          updatePaymentSubmitState();
        }
      }


      /* =========================================================
         EVENTOS
      ========================================================= */

      loginForm
        ?.addEventListener(
          "submit",
          (event) => {
            event.preventDefault();
            handleLogin();
          }
        );


      googleLoginButton
        ?.addEventListener(
          "click",
          handleGoogleLogin
        );


      verifyEmailResend
        ?.addEventListener(
          "click",
          async () => {

            if (
              verifyEmailResend.disabled
            ) {
              return;
            }


            const originalText =
              verifyEmailResend.textContent;


            verifyEmailResend.disabled =
              true;

            verifyEmailResend.textContent =
              "Enviando…";


            try {

              await window.PeguuAuth
                ?.requestEmailVerification?.();


              showVerifyEmailBanner(
                "Te enviamos un correo de verificación. Revisa tu bandeja de entrada o correo no deseado."
              );


              verifyEmailResend.textContent =
                "Correo enviado";

            } catch (error) {

              verifyEmailResend.disabled =
                false;

              verifyEmailResend.textContent =
                originalText;


              showVerifyEmailBanner(
                error?.status === 429
                  ? "Demasiados intentos. Espera un momento y vuelve a intentar."
                  : (
                      error?.message ||
                      "No fue posible reenviar el correo de verificación."
                    )
              );

            }

          }
        );


      window.addEventListener(
        "peguu:customer-updated",
        (event) => {

          const session =
            event.detail ||
            window.PeguuAuth
              ?.getSession?.();


          syncVerifyEmailBanner(
            session
          );

        }
      );


      window.addEventListener(
        "peguu:customer-authenticated",
        async (event) => {

          const session =
            event.detail ||
            window.PeguuAuth
              ?.getSession?.();


          if (session?.token) {
            try {
              await window.PeguuBilling
                ?.loadProfile?.(
                  session
                );
            } catch (error) {
              console.warn(
                "PEGUU: no fue posible consultar el perfil fiscal.",
                {
                  status:
                    error?.status ||
                    null
                }
              );
            }
          }


          renderAuthenticationState({
            isAuthenticated:
              Boolean(
                session?.token &&
                session?.customer
              ),

            customer:
              session?.customer ||
              null
          });

        }
      );


      window.addEventListener(
        "peguu:customer-logged-out",
        () => {

          window.PeguuBilling
            ?.resetProfileState?.();

          renderAuthenticationState({
            isAuthenticated: false
          });

        }
      );


      window.addEventListener(
        "peguu:auth-state-changed",
        (event) => {

          const session =
            event.detail?.session ||
            null;


          renderAuthenticationState({
            isAuthenticated:
              Boolean(
                event.detail
                  ?.authenticated &&
                session?.customer
              ),

            customer:
              session?.customer ||
              null
          });

        }
      );


      guestButton
        ?.addEventListener(
          "click",
          handleGuest
        );


      returnToLoginButton
        ?.addEventListener(
          "click",
          returnToAccess
        );


      passwordToggle
        ?.addEventListener(
          "click",
          togglePassword
        );


      invoiceCheckbox
        ?.addEventListener(
          "change",
          () => {
            if (paymentValidationShown) {
              refreshPaymentValidationAlert();
            }
            updatePaymentSubmitState();
          }
        );


      paymentSubmit
        ?.addEventListener(
          "click",
          handlePaymentSubmit
        );


      window.addEventListener(
        "pageshow",
        (event) => {
          if (!event.persisted) {
            return;
          }

          isCreatingOrder = false;
          orderCreated = false;
          root.dataset.paymentReady =
            "false";
          updatePaymentSubmitState();
        }
      );


      /* =========================
         DATOS FISCALES GUARDADOS
      ========================= */

      window.addEventListener(
        "peguu:billing-saved",
        () => {

          if (invoiceCheckbox) {
            invoiceCheckbox.checked =
              true;
          }


          updatePaymentSubmitState();

        }
      );


      window.addEventListener(
        "peguu:billing-profile-loaded",
        updatePaymentSubmitState
      );


      [
        contactEmailInput,
        contactPhoneInput
      ]
        .filter(Boolean)
        .forEach((input) => {
          input.addEventListener(
            "input",
            () => {
              if (input === contactPhoneInput) {
                input.value = input.value
                  .replace(/\D/g, "")
                  .slice(0, 10);
              }

              syncContactFieldValueState(input);
              persistContactDetails();

              if (paymentValidationShown) {
                validateContactDetails();
                refreshPaymentValidationAlert();
              } else {
                setContactFieldError(
                  input,
                  input === contactEmailInput
                    ? contactEmailError
                    : contactPhoneError,
                  true
                );
              }

              updatePaymentSubmitState();
            }
          );
        });


      contactPreferenceInputs
        .forEach((input) => {

          input.addEventListener(
            "change",
            () => {
              if (paymentValidationShown) {
                validateContactPreference();
                refreshPaymentValidationAlert();
              } else {
                clearContactPreferenceError();
              }
              updatePaymentSubmitState();
            }
          );

        });

      /* =========================
         ENTER LOGIN
      ========================= */

      [
        emailInput,
        passwordInput
      ]
        .filter(Boolean)
        .forEach((input) => {

          input.addEventListener(
            "keydown",
            (event) => {

              if (
                event.key !==
                "Enter"
              ) {
                return;
              }


              event.preventDefault();


              handleLogin();

            }
          );

        });


      /* =========================================================
         ESTADO INICIAL
      ========================================================= */

      syncPasswordToggle();


      syncInvoiceCheckbox();


      syncContactFieldValueState(
        contactEmailInput
      );
      syncContactFieldValueState(
        contactPhoneInput
      );


      updatePaymentSubmitState();


      if (isSamplePackCheckout) {
        getSamplePackMaterial()
          .then(() => {
            updatePaymentSubmitState();
          })
          .catch((error) => {
            console.error(
              "PEGUU: No fue posible preparar la compra del paquete de muestras.",
              error
            );
            updatePaymentSubmitState();
          });
      }


      root.peguuStickerPaymentRefresh =
        restoreAuthenticationState;


      root.peguuStickerPaymentPosition =
        () => {
          root.removeAttribute(
            "data-defer-initial-position"
          );

          if (
            root.dataset
              .paymentAuthPending ===
              "true"
          ) {
            return;
          }

          hasPositionedInitialState = true;

          scrollToPaymentState(
            getCheckoutScrollTarget()
          );
        };


      restoreAuthenticationState();

    });

  }

  async function refreshPeguuStickerPayment(
    scope = document
  ) {
    const roots =
      scope.matches?.(
        "[data-peguu-sticker-payment]"
      )
        ? [scope]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-sticker-payment]"
            )
          );

    await Promise.all(
      roots.map((root) => {
        if (
          root.dataset.paymentInitialized !== "true" ||
          typeof root.peguuStickerPaymentRefresh !== "function"
        ) {
          return Promise.resolve();
        }

        return root.peguuStickerPaymentRefresh();
      })
    );
  }


  window.PeguuStickerPayment = Object.freeze({
    init: initPeguuStickerPayment,
    refresh: refreshPeguuStickerPayment,
  });


  /* =========================================================
     DOM READY
  ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(
      "DOMContentLoaded",
      () => {

        initPeguuStickerPayment();

      }
    );

  } else {

    initPeguuStickerPayment();

  }


  /* =========================================================
     SHOPIFY THEME EDITOR
  ========================================================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {

      initPeguuStickerPayment(
        event.target
      );

    }
  );

})();
